/**
 * Whether the frontmost insertion mark should warn about unloaded older
 * events before inserting "ganz vorne" — PRD docs/prd/attribution-korrektur.md
 * Story S6, Epic A4.
 *
 * "Ganz an den Anfang" is only really the anfang once every event is
 * loaded: the journey shows a 35-day hot window by default, older events
 * sit in long-term storage (BigQuery) until fetched on demand. Warns,
 * never blocks — inserting stays possible with the hint standing (AC).
 *
 * Only the mark with no previous neighbor (`prevAt === null`) can ever be
 * preceded by unloaded history — every other mark already has a loaded
 * neighbor right before it, so nothing older could hide behind it.
 * `olderCount` is the existing completeness signal from the detail view
 * (Issue: "ist in der Detailansicht bereits vorhanden und wird
 * wiederverwendet", CustomerDetail's `olderInBq`) — not re-derived here.
 *
 * Deliberately not a hint about rule effect (out of scope, epic) — this is
 * about a data gap, not about attribution-rule behavior.
 */
export function shouldWarnAboutOlderEvents(
  prevAt: string | null,
  olderCount: number,
): boolean {
  return prevAt === null && olderCount > 0;
}

/**
 * Whether an insertion mark should render at all, given the section-wide
 * visibility toggle ("Touchpoint einfügen" button, TASK-75) and whether this
 * particular mark's own form is currently open.
 *
 * An already-open form must not vanish silently when the toggle is switched
 * off (issue AC) — it keeps rendering until the operator submits or cancels
 * it explicitly, so `formOpen` alone is enough to keep a mark alive even
 * while `visible` is false.
 */
export function shouldRenderInsertMark(
  visible: boolean,
  formOpen: boolean,
): boolean {
  return visible || formOpen;
}
